import { useEffect, useMemo, useRef, useState, createContext, useContext } from "react";
import {
  ArrowRight,
  Check,
  X,
  Phone,
  MessageCircle,
  Sparkles,
  Target,
  Brain,
  LayoutDashboard,
  Send,
  GraduationCap,
  Compass,
  Palette,
  Users,
  AlertTriangle,
  TrendingDown,
  EyeOff,
  Clock4,
  Quote,
  ChevronDown,
  BadgeCheck,
  Calculator,
  Activity,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Slider } from "@/components/ui/slider";
import SaasInquiryDialog from "@/components/SaasInquiryDialog";
import CalBookingDialog from "@/components/CalBookingDialog";
import MedaAiHero from "@/components/MedaAiHero";

/* -------------------------------------------------------------- */
/*  Inquiry context — single dialog instance, opened by any CTA   */
/* -------------------------------------------------------------- */
const InquiryCtx = createContext(null);
function useInquiry() {
  return useContext(InquiryCtx);
}

const CTA_PRESETS = {
  book_demo: {
    title: "Book a 30-min demo",
    subtitle: "We'll set up a custom-branded sandbox before the call.",
  },
  callback: {
    title: "Request a call back",
    subtitle: "A senior team member will call you at the time you pick.",
  },
  download_deck: {
    title: "Get the sales deck",
    subtitle: "We'll WhatsApp you the PDF and follow up — no email spam.",
  },
  case_study: {
    title: "Read the Sunrise playbook",
    subtitle: "We'll send the case study + a private walkthrough invite.",
  },
  unfair_advantage: {
    title: "Get your unfair advantage",
    subtitle: "30-min demo · custom-branded sandbox · no commitment.",
  },
};

function InquiryProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [cta, setCta] = useState("book_demo");
  const openWith = (key = "book_demo") => {
    setCta(key);
    setOpen(true);
  };
  const openCalendar = () => {
    setOpen(false);
    setCalOpen(true);
  };
  const preset = CTA_PRESETS[cta] || CTA_PRESETS.book_demo;
  return (
    <InquiryCtx.Provider value={{ openWith, openCalendar }}>
      {children}
      <SaasInquiryDialog
        open={open}
        onOpenChange={setOpen}
        cta={cta}
        title={preset.title}
        subtitle={preset.subtitle}
        onPickSlot={openCalendar}
      />
      <CalBookingDialog open={calOpen} onOpenChange={setCalOpen} />
    </InquiryCtx.Provider>
  );
}

/* -------------------------------------------------------------- */
/*  Sticky luxury nav (appears after hero on scroll-down)         */
/* -------------------------------------------------------------- */
function StickyNav() {
  const [visible, setVisible] = useState(false);
  const lastY = useRef(0);
  const { openWith } = useInquiry();
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const goingDown = y > lastY.current;
      setVisible(goingDown && y > 720);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      data-testid="saas-sticky-nav"
      className={`fixed top-0 inset-x-0 z-40 transition-transform duration-500 ${
        visible ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      <div className="bg-[#0A2540]/92 backdrop-blur-md border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-14 flex items-center justify-between">
          <a href="#hero" className="flex items-center gap-2 text-white">
            <span className="font-serif-display text-xl tracking-tight">
              Med<span className="text-gold italic">Admits</span>
            </span>
          </a>
          <button
            onClick={() => openWith("book_demo")}
            data-testid="sticky-nav-book-demo"
            className="text-xs uppercase tracking-[0.18em] text-[#0A2540] bg-gold hover:bg-[#d8b977] transition-colors px-5 py-2.5 rounded-full font-medium"
          >
            Book demo
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Hero — asymmetric split + animated dashboard mockup            */
/* -------------------------------------------------------------- */
function HeroDashboard() {
  const frames = ["pipeline", "analytics", "whatsapp"];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % frames.length), 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative w-full">
      {/* floating decorative */}
      <div className="absolute -top-8 -right-6 h-32 w-32 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-[#0A2540]/10 blur-3xl pointer-events-none" />

      <div className="relative rounded-[22px] border border-[#0A2540]/10 bg-white shadow-[0_30px_80px_-30px_rgba(10,37,64,0.35)] overflow-hidden">
        {/* window chrome */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-[#0A2540]/8 bg-[#FAF9F6]">
          <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
          <div className="ml-3 flex-1 max-w-md">
            <div className="text-[11px] font-mono-clock text-[#0A2540]/55 truncate">
              app.medadmits.com / {frames[idx]}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {frames.map((f, i) => (
              <span
                key={f}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === idx ? "w-6 bg-gold" : "w-2 bg-[#0A2540]/15"
                }`}
              />
            ))}
          </div>
        </div>

        {/* frames */}
        <div className="relative h-[340px] sm:h-[380px]">
          {/* frame 1 — Pipeline */}
          <div
            className={`absolute inset-0 p-5 transition-opacity duration-700 ${
              idx === 0 ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#0A2540]/55">
              Lead pipeline · Today
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {[
                { label: "New", count: 38, c: "bg-[#0A2540]" },
                { label: "Contacted", count: 21, c: "bg-[#1d4870]" },
                { label: "Counselling", count: 14, c: "bg-[#3a6a9c]" },
                { label: "Closed", count: 7, c: "bg-emerald-700" },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-[#0A2540]/10 p-3">
                  <div className={`h-1 w-8 rounded-full ${s.c}`} />
                  <div className="mt-3 text-2xl font-serif-display text-[#0A2540]">
                    {s.count}
                  </div>
                  <div className="text-[11px] text-[#0A2540]/55">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              {[
                { n: "Aarav S.", score: 92, temp: "Hot", neet: 612 },
                { n: "Priya K.", score: 86, temp: "Warm", neet: 548 },
                { n: "Mihir P.", score: 71, temp: "Warm", neet: 502 },
                { n: "Sana R.", score: 58, temp: "Cold", neet: 421 },
              ].map((l, i) => (
                <div
                  key={l.n}
                  className="flex items-center justify-between rounded-md border border-[#0A2540]/8 bg-[#FAF9F6] px-3 py-2.5"
                  style={{ animation: `saasReveal 600ms ${i * 80}ms both` }}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-7 w-7 rounded-full bg-[#0A2540] text-white flex items-center justify-center text-[10px] font-semibold">
                      {l.n.split(" ").map((p) => p[0]).join("")}
                    </div>
                    <div>
                      <div className="text-sm text-[#0A2540] font-medium">{l.n}</div>
                      <div className="text-[10px] text-[#0A2540]/50">
                        NEET {l.neet} · UTM facebook/scholarship
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono-clock text-[#0A2540]/70">
                      {l.score}
                    </span>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full ${
                        l.temp === "Hot"
                          ? "bg-red-50 text-red-700 pulse-hot"
                          : l.temp === "Warm"
                          ? "bg-amber-50 text-amber-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {l.temp}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* frame 2 — Analytics */}
          <div
            className={`absolute inset-0 p-5 transition-opacity duration-700 ${
              idx === 1 ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#0A2540]/55">
              Funnel attribution · Last 30 days
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3">
              {[
                { l: "Leads", v: "1,284" },
                { l: "Tested", v: "612" },
                { l: "Closed", v: "47" },
              ].map((k) => (
                <div key={k.l} className="rounded-lg border border-[#0A2540]/10 p-3">
                  <div className="text-[11px] text-[#0A2540]/55">{k.l}</div>
                  <div className="text-2xl font-serif-display text-[#0A2540]">{k.v}</div>
                </div>
              ))}
            </div>
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#0A2540]/55 mb-2">
                Source — UTM
              </div>
              <div className="space-y-2">
                {[
                  { s: "facebook", v: 78 },
                  { s: "instagram", v: 56 },
                  { s: "google", v: 42 },
                  { s: "referral", v: 24 },
                ].map((b) => (
                  <div key={b.s} className="flex items-center gap-3">
                    <div className="w-20 text-[11px] text-[#0A2540]/65">{b.s}</div>
                    <div className="flex-1 h-2 rounded-full bg-[#0A2540]/8 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#0A2540] to-[#C9A961] rounded-full"
                        style={{ width: `${b.v}%` }}
                      />
                    </div>
                    <div className="w-8 text-right text-[11px] font-mono-clock text-[#0A2540]/65">
                      {b.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* frame 3 — WhatsApp */}
          <div
            className={`absolute inset-0 p-5 transition-opacity duration-700 ${
              idx === 2 ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#0A2540]/55">
              WhatsApp delivery · Scholarship report
            </div>
            <div className="mt-4 mx-auto max-w-md rounded-2xl bg-[#0a141a] p-4 shadow-inner">
              <div className="text-[10px] text-emerald-300/80 mb-2">
                + To: +91 98•••• 4421 · MedAdmits AI
              </div>
              <div className="space-y-2">
                <div className="ml-auto max-w-[80%] bg-[#0A2540] text-white text-xs px-3 py-2 rounded-2xl rounded-tr-sm">
                  Hi Aarav, your scholarship slab is{" "}
                  <span className="text-gold font-semibold">42% off</span> based on NEET 612.
                </div>
                <div className="ml-auto max-w-[80%] bg-[#0A2540] text-white text-xs px-3 py-2 rounded-2xl rounded-tr-sm">
                  Detailed PDF attached · Crimea Med · Tbilisi State
                </div>
                <div className="ml-auto max-w-[60%] bg-emerald-700 text-white text-xs px-3 py-2 rounded-2xl rounded-tr-sm flex items-center gap-2">
                  <BadgeCheck className="h-3.5 w-3.5" /> Read · 2:14 PM
                </div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              {[
                { l: "Sent", v: "612" },
                { l: "Read", v: "541" },
                { l: "Replied", v: "118" },
              ].map((m) => (
                <div key={m.l} className="rounded-md border border-[#0A2540]/10 py-2">
                  <div className="text-base font-serif-display text-[#0A2540]">{m.v}</div>
                  <div className="text-[10px] text-[#0A2540]/55">{m.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroSection() {
  const { openWith } = useInquiry();
  return (
    <section
      id="hero"
      data-testid="saas-hero"
      className="relative overflow-hidden bg-ivory grain-soft"
    >
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-20 lg:pt-28 pb-16 lg:pb-24">
        {/* Top brand bar */}
        <div className="flex items-center justify-between mb-12">
          <a href="#hero" className="flex items-center gap-2 text-[#0A2540]">
            <span className="font-serif-display text-2xl">
              Med<span className="italic text-gold">Admits</span>
            </span>
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT — copy */}
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold">
              <span className="h-px w-8 bg-gold/60" />
              10× your MBBS &amp; NEET Admissions
            </div>

            <h1 className="mt-6 font-serif-display text-[44px] sm:text-[58px] lg:text-[68px] leading-[1.02] text-[#0A2540]">
              Launch your own
              <br />
              Medical Edtech Platform
              <span className="italic text-gold"> — like Top 1% Brands.</span>
            </h1>

            <p className="mt-7 max-w-xl text-[17px] leading-[1.6] text-[#0A2540]/70">
              Run your business like a Series-B startup with the white-label platform
              built to <span className="text-[#0A2540] font-medium">capture, score, nudge,
              and close</span> more admissions — a premium console your team will actually use.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={() => openWith("book_demo")}
                data-testid="hero-cta-book-demo"
                className="btn-luxury inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-medium tracking-wide"
              >
                Book a 30-min demo
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => openWith("callback")}
                data-testid="hero-cta-callback"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full text-sm font-medium tracking-wide border border-[#0A2540]/20 text-[#0A2540] hover:border-[#0A2540] hover:bg-white transition-all"
              >
                Request a call back
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-6 flex items-center gap-2 text-xs text-[#0A2540]/60">
              <ShieldCheck className="h-4 w-4 text-gold" />
              Live in just 7 days · Custom-branded sandbox · No credit card
            </div>
          </div>

          {/* RIGHT — animated dashboard */}
          <div className="lg:col-span-6">
            <HeroDashboard />
          </div>
        </div>
      </div>

      {/* Trust strip — universities marquee */}
      <div className="relative z-10 border-y border-[#0A2540]/8 bg-white/60 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-5 flex items-center gap-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#0A2540]/55 hidden sm:block whitespace-nowrap">
            Trusted by counsellors placing students at
          </div>
          <div className="flex-1 overflow-hidden relative">
            <div className="flex marquee-track gap-12 whitespace-nowrap">
              {[...Array(2)].map((_, k) => (
                <div key={k} className="flex gap-12 shrink-0">
                  {[
                    "Crimea Federal University",
                    "Tbilisi State Medical",
                    "Samarkand State Medical",
                    "Osh State Medical",
                    "Bashkir State Medical",
                    "Kazakh National Medical",
                    "Kyrgyz State Medical",
                  ].map((u) => (
                    <span
                      key={u + k}
                      className="text-[#0A2540]/55 text-sm font-serif-display whitespace-nowrap"
                    >
                      {u}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- */
/*  Section: Pain (black band)                                    */
/* -------------------------------------------------------------- */
function PainSection() {
  const cards = [
    {
      Icon: TrendingDown,
      title: "Low quality leads",
      body:
        "Your counsellors waste hours on cold enquiries that were never going to enrol.",
    },
    {
      Icon: EyeOff,
      title: "No idea where ad spend goes",
      body:
        "Spent ₹3 lakh on Facebook last quarter. Closed 4 students. Or was it 6? Nobody really knows.",
    },
    {
      Icon: AlertTriangle,
      title: "No tracking for counsellors",
      body:
        "No scoreboard, no accountability, no clarity on who is actually closing admissions.",
    },
    {
      Icon: Clock4,
      title: "Parents pick whoever WhatsApps first",
      body:
        "Your follow-up is 12 hours behind your competitor. You lose the parent every single time.",
    },
  ];
  return (
    <section
      data-testid="saas-pain"
      className="relative bg-[#06121e] grain-soft grain-dark text-white"
    >
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold flex items-center gap-2">
          <span className="h-px w-8 bg-gold/60" /> The reality on the ground
        </div>
        <h2 className="mt-5 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] max-w-3xl">
          Spending lakhs on social media ads but still
          <span className="italic text-gold"> not getting enough admissions?</span>
        </h2>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map(({ Icon, title, body }) => (
            <div
              key={title}
              className="group rounded-xl border border-white/8 bg-white/[0.03] p-7 hover:bg-white/[0.06] hover:border-gold/40 transition-all duration-500"
            >
              <div className="h-10 w-10 rounded-lg bg-red-500/12 border border-red-400/25 flex items-center justify-center text-red-300 group-hover:scale-110 transition-transform duration-500">
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-5 font-serif-display text-xl text-white">{title}</div>
              <div className="mt-3 text-sm text-white/65 leading-relaxed">{body}</div>
            </div>
          ))}
        </div>

        <div className="mt-14 max-w-3xl">
          <p className="text-base sm:text-lg text-white/75 leading-relaxed">
            Sound familiar? You're not alone. Almost every MBBS consultancy in India operates
            this way. We built <span className="text-gold">MedAdmits AI</span> so you don't have to.
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- */
/*  Section: Solution + Feature grid                              */
/* -------------------------------------------------------------- */
function SolutionSection() {
  const features = [
    { Icon: GraduationCap, t: "Scholarship Test Funnel", d: "Generate high-intent NEET aspirant leads through online scholarship tests." },
    { Icon: Brain, t: "AI Lead Scoring", d: "Automatically identify serious students based on scores & behaviour." },
    { Icon: LayoutDashboard, t: "AI CRM", d: "Manage leads, counsellors, follow-ups & admissions in one dashboard." },
    { Icon: MessageCircle, t: "WhatsApp Automation", d: "Send reminders, counselling invites & scholarship reports automatically." },
    { Icon: Activity, t: "Student Dashboard", d: "Students view test results, reports & counselling recommendations." },
    { Icon: Compass, t: "NEET College Predictor", d: "Capture leads from MBBS-in-India aspirants searching for colleges." },
    { Icon: Palette, t: "White-label Branding", d: "Your logo, your domain, your consultancy brand — fully isolated tenant." },
    { Icon: Users, t: "Parent Data Collection", d: "Capture parent numbers and improve closure rates dramatically." },
  ];
  return (
    <section
      id="product"
      data-testid="saas-solution"
      className="relative bg-ivory grain-soft"
    >
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold flex items-center gap-2">
              <span className="h-px w-8 bg-gold/60" /> The solution
            </div>
            <h2 className="mt-5 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-[#0A2540]">
              One SaaS to automate your entire
              <span className="italic text-gold"> NEET &amp; MBBS admission funnel.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#0A2540]/70 text-base leading-relaxed">
              From lead capture to scholarship slab, college shortlisting to closure —
              run the whole motion on a console designed specifically for medical edtech
              consultancies. No bolt-ons. No retro-fitted CRMs.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#0A2540]/8 rounded-2xl overflow-hidden border border-[#0A2540]/8">
          {features.map(({ Icon, t, d }, i) => (
            <div
              key={t}
              className="group bg-ivory p-7 hover:bg-white transition-colors duration-500"
              data-testid={`feature-card-${i}`}
            >
              <div className="h-10 w-10 rounded-lg bg-[#0A2540]/5 border border-[#0A2540]/10 flex items-center justify-center text-[#0A2540] group-hover:bg-gold/15 group-hover:border-gold/40 group-hover:text-[#0A2540] transition-colors duration-500">
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-5 font-serif-display text-lg text-[#0A2540]">{t}</div>
              <div className="mt-2 text-[13.5px] text-[#0A2540]/65 leading-relaxed">{d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- */
/*  Section: Live product showcase (tabs)                         */
/* -------------------------------------------------------------- */
function ProductShowcaseSection() {
  return (
    <section
      data-testid="saas-showcase"
      className="relative bg-white"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold flex items-center gap-2">
          <span className="h-px w-8 bg-gold/60" /> Inside the platform
        </div>
        <h2 className="mt-5 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-[#0A2540] max-w-3xl">
          Five surfaces. <span className="italic text-gold">One console.</span>
        </h2>

        <Tabs defaultValue="pipeline" className="mt-12">
          <TabsList className="bg-[#FAF9F6] border border-[#0A2540]/10 p-1 h-auto flex flex-wrap gap-1">
            {[
              { v: "pipeline", l: "Pipeline" },
              { v: "analytics", l: "Analytics" },
              { v: "campaigns", l: "Campaigns" },
              { v: "tasks", l: "Tasks" },
              { v: "mobile", l: "Mobile" },
            ].map((t) => (
              <TabsTrigger
                key={t.v}
                value={t.v}
                data-testid={`showcase-tab-${t.v}`}
                className="px-5 py-2.5 text-sm rounded-md data-[state=active]:bg-[#0A2540] data-[state=active]:text-white"
              >
                {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          <ShowcasePane
            value="pipeline"
            caption="Auto-scored leads, claimed by counsellors, closed in 4 stages."
            mock={<MockPipeline />}
          />
          <ShowcasePane
            value="analytics"
            caption="Funnel, UTM attribution, score histogram — all live."
            mock={<MockAnalytics />}
          />
          <ShowcasePane
            value="campaigns"
            caption="Save 'Hot Punjab leads' once. Run weekly. 10 WhatsApp tabs in one click."
            mock={<MockCampaigns />}
          />
          <ShowcasePane
            value="tasks"
            caption="Auto-tasks on every status change. Counsellors stop forgetting follow-ups."
            mock={<MockTasks />}
          />
          <ShowcasePane
            value="mobile"
            caption="Counsellors live on phones. So does your CRM."
            mock={<MockMobile />}
          />
        </Tabs>
      </div>
    </section>
  );
}

function ShowcasePane({ value, caption, mock }) {
  return (
    <TabsContent value={value} className="mt-8">
      <div className="rounded-2xl border border-[#0A2540]/10 bg-[#FAF9F6] overflow-hidden shadow-[0_30px_70px_-30px_rgba(10,37,64,0.25)]">
        {mock}
      </div>
      <div className="mt-5 text-[#0A2540]/70 text-base">{caption}</div>
    </TabsContent>
  );
}

function MockPipeline() {
  return (
    <div className="p-6 lg:p-8">
      <div className="grid grid-cols-4 gap-3">
        {[
          { l: "New", c: 38 },
          { l: "Contacted", c: 21 },
          { l: "Counselling", c: 14 },
          { l: "Closed", c: 7 },
        ].map((s) => (
          <div key={s.l} className="rounded-lg border border-[#0A2540]/10 bg-white p-4">
            <div className="text-[10px] uppercase tracking-wider text-[#0A2540]/55">{s.l}</div>
            <div className="mt-2 font-serif-display text-3xl text-[#0A2540]">{s.c}</div>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-lg border border-[#0A2540]/10 bg-white">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-[#0A2540]/55 border-b border-[#0A2540]/8">
          <div className="col-span-3">Lead</div>
          <div className="col-span-2">NEET</div>
          <div className="col-span-2">Score</div>
          <div className="col-span-2">Temp</div>
          <div className="col-span-3">Counsellor</div>
        </div>
        {[
          ["Aarav S.", 612, 92, "Hot", "Riya M."],
          ["Priya K.", 548, 86, "Warm", "Karan D."],
          ["Mihir P.", 502, 71, "Warm", "Riya M."],
          ["Sana R.", 421, 58, "Cold", "—"],
          ["Vikram J.", 591, 88, "Hot", "Karan D."],
        ].map(([n, neet, score, temp, c]) => (
          <div
            key={n}
            className="grid grid-cols-12 gap-2 px-4 py-3 text-sm text-[#0A2540] border-b border-[#0A2540]/6 last:border-0"
          >
            <div className="col-span-3 font-medium">{n}</div>
            <div className="col-span-2 font-mono-clock text-[#0A2540]/70">{neet}</div>
            <div className="col-span-2 font-mono-clock">{score}</div>
            <div className="col-span-2">
              <span
                className={`text-[11px] px-2 py-0.5 rounded-full ${
                  temp === "Hot"
                    ? "bg-red-50 text-red-700"
                    : temp === "Warm"
                    ? "bg-amber-50 text-amber-700"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {temp}
              </span>
            </div>
            <div className="col-span-3 text-[#0A2540]/70">{c}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockAnalytics() {
  return (
    <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 rounded-lg border border-[#0A2540]/10 bg-white p-5">
        <div className="text-[10px] uppercase tracking-wider text-[#0A2540]/55">Funnel — last 30 days</div>
        <div className="mt-4 space-y-3">
          {[
            { l: "Visited landing", v: 100, n: "4,829" },
            { l: "Registered", v: 62, n: "1,284" },
            { l: "Took test", v: 38, n: "612" },
            { l: "Counselled", v: 12, n: "164" },
            { l: "Enrolled", v: 4, n: "47" },
          ].map((b) => (
            <div key={b.l} className="flex items-center gap-4">
              <div className="w-32 text-[12px] text-[#0A2540]/65">{b.l}</div>
              <div className="flex-1 h-3 rounded-full bg-[#0A2540]/8 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#0A2540] via-[#1d4870] to-[#C9A961]"
                  style={{ width: `${b.v}%` }}
                />
              </div>
              <div className="w-16 text-right text-[12px] font-mono-clock text-[#0A2540]/70">
                {b.n}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-lg border border-[#0A2540]/10 bg-white p-5">
        <div className="text-[10px] uppercase tracking-wider text-[#0A2540]/55">Counsellor leaderboard</div>
        <div className="mt-3 space-y-2">
          {[
            ["Riya M.", 24, "🥇"],
            ["Karan D.", 19, "🥈"],
            ["Anita V.", 14, "🥉"],
            ["Suresh L.", 9, ""],
          ].map(([n, c, e]) => (
            <div
              key={n}
              className="flex items-center justify-between rounded-md bg-[#FAF9F6] px-3 py-2"
            >
              <div className="text-sm text-[#0A2540]">{n}</div>
              <div className="text-sm font-mono-clock text-[#0A2540]/70">{c} closed</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MockCampaigns() {
  return (
    <div className="p-6 lg:p-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[
        { n: "Hot Punjab leads", f: "state=Punjab · score≥80", r: 14 },
        { n: "NEET 550+ unattended", f: "neet≥550 · counsellor=null", r: 8 },
        { n: "30-day silent leads", f: "last_contact>30d", r: 5 },
        { n: "Crimea preference", f: "preferred_country=Crimea", r: 11 },
        { n: "Parent uncalled", f: "parent_phone=set · status=new", r: 19 },
        { n: "Drop-off after test", f: "tested · not counselled", r: 23 },
      ].map((c) => (
        <div key={c.n} className="rounded-lg border border-[#0A2540]/10 bg-white p-5">
          <div className="font-serif-display text-lg text-[#0A2540]">{c.n}</div>
          <div className="mt-1 text-[12px] text-[#0A2540]/55 font-mono-clock">{c.f}</div>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-[11px] text-[#0A2540]/60">{c.r} runs</span>
            <span className="text-[11px] uppercase tracking-wider text-gold">Run →</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function MockTasks() {
  return (
    <div className="p-6 lg:p-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="rounded-lg border border-[#0A2540]/10 bg-white p-5">
          <div className="text-[10px] uppercase tracking-wider text-[#0A2540]/55">Today · 4</div>
          <div className="mt-3 space-y-2">
            {[
              "Call Aarav S. — scholarship slab follow-up",
              "WhatsApp Priya K. — visa documents",
              "Counsel Mihir P. — Crimea vs Tbilisi",
              "Parent call — Sana R. (mother)",
            ].map((t) => (
              <div key={t} className="flex items-center gap-3 text-sm text-[#0A2540]">
                <span className="h-4 w-4 rounded border border-[#0A2540]/30" />
                {t}
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50/40 p-5">
          <div className="text-[10px] uppercase tracking-wider text-red-700">Overdue · 2</div>
          <div className="mt-3 space-y-2">
            {[
              "Vikram J. — counselling rescheduled twice",
              "Anita's parent — pending call back",
            ].map((t) => (
              <div key={t} className="flex items-center gap-3 text-sm text-red-900">
                <span className="h-4 w-4 rounded border border-red-300" />
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MockMobile() {
  return (
    <div className="p-6 lg:p-10 flex justify-center">
      <div className="w-[280px] rounded-[2rem] bg-[#0A2540] p-3 shadow-2xl">
        <div className="rounded-[1.5rem] bg-white overflow-hidden">
          <div className="bg-[#0A2540] text-white px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider opacity-70">Today · Riya M.</div>
            <div className="font-serif-display text-lg">12 hot leads</div>
          </div>
          <div className="p-3 space-y-2">
            {[
              { n: "Aarav S.", t: "NEET 612" },
              { n: "Vikram J.", t: "NEET 591" },
              { n: "Priya K.", t: "NEET 548" },
            ].map((l) => (
              <div
                key={l.n}
                className="rounded-lg border border-[#0A2540]/10 p-3 flex items-center justify-between"
              >
                <div>
                  <div className="text-sm text-[#0A2540] font-medium">{l.n}</div>
                  <div className="text-[11px] text-[#0A2540]/55">{l.t}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="h-9 w-9 rounded-full bg-[#0A2540] text-white flex items-center justify-center">
                    <Phone className="h-4 w-4" />
                  </button>
                  <button className="h-9 w-9 rounded-full bg-emerald-600 text-white flex items-center justify-center">
                    <MessageCircle className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Section: Differentiators                                       */
/* -------------------------------------------------------------- */
function DifferentiatorsSection() {
  const rows = [
    ["NEET-aware lead scoring", false, true],
    ["Built-in scholarship test", false, true],
    ["WhatsApp PDF delivery", false, true],
    ["White-label per consultancy", "Enterprise tier $$$", "Included"],
    ["Counsellor scoreboard", false, true],
    ["INR + NEET cutoff data", false, true],
  ];
  return (
    <section
      data-testid="saas-diff"
      className="relative bg-ivory grain-soft"
    >
      <div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold flex items-center gap-2">
          <span className="h-px w-8 bg-gold/60" /> Why MedAdmits AI
        </div>
        <h2 className="mt-5 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-[#0A2540] max-w-3xl">
          Built for one industry. <span className="italic text-gold">Not one hundred.</span>
        </h2>
        <p className="mt-5 max-w-2xl text-[#0A2540]/70">
          HubSpot is built for SaaS sales teams. Salesforce is built for the Fortune 500.
          MedAdmits AI is built for the consultancy owner who knows the difference between
          Crimea Med and Tbilisi State.
        </p>

        <div className="mt-12 rounded-2xl border border-[#0A2540]/10 overflow-hidden bg-white shadow-[0_20px_60px_-25px_rgba(10,37,64,0.2)]">
          <div className="grid grid-cols-12 px-6 py-4 bg-[#FAF9F6] border-b border-[#0A2540]/10 text-[11px] uppercase tracking-[0.18em] text-[#0A2540]/65">
            <div className="col-span-6">Capability</div>
            <div className="col-span-3 text-center">Generic CRM</div>
            <div className="col-span-3 text-center text-[#0A2540]">MedAdmits AI</div>
          </div>
          {rows.map(([cap, generic, ours], i) => (
            <div
              key={cap}
              className={`grid grid-cols-12 px-6 py-5 items-center ${
                i % 2 === 0 ? "bg-white" : "bg-[#FAF9F6]/40"
              } border-b border-[#0A2540]/6 last:border-0`}
            >
              <div className="col-span-6 text-sm sm:text-base text-[#0A2540]">{cap}</div>
              <div className="col-span-3 text-center text-sm">
                {typeof generic === "string" ? (
                  <span className="text-[#0A2540]/55">{generic}</span>
                ) : (
                  <X className="h-5 w-5 text-red-500/80 inline" />
                )}
              </div>
              <div className="col-span-3 text-center text-sm">
                {typeof ours === "string" ? (
                  <span className="text-gold font-medium">{ours}</span>
                ) : (
                  <Check className="h-5 w-5 text-gold inline" strokeWidth={2.5} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- */
/*  Section: ROI-focused (replaces pricing #1)                    */
/* -------------------------------------------------------------- */
function RoiFocusSection() {
  const items = [
    "Scholarship campaign",
    "Student data collection system",
    "College predictor",
    "Admission pipeline",
    "CRM automation workflow",
  ];
  return (
    <section
      data-testid="saas-roi-focus"
      className="relative bg-[#0A2540] grain-soft grain-dark text-white"
    >
      <div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold flex items-center gap-2">
              <span className="h-px w-8 bg-gold/60" /> ROI re-frame
            </div>
            <h2 className="mt-5 font-serif-display text-3xl sm:text-4xl lg:text-[52px] leading-[1.05]">
              Stop buying leads.
              <br />
              <span className="italic text-gold">Start building an admission funnel.</span>
            </h2>
            <p className="mt-7 text-white/70 max-w-lg leading-relaxed">
              With MedAdmits AI every ad campaign you run stops being just another enquiry
              form — and becomes an asset you own forever.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-7">
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/50">
                Every campaign becomes
              </div>
              <ul className="mt-5 space-y-4">
                {items.map((it, i) => (
                  <li key={it} className="flex items-start gap-4 group">
                    <span className="editorial-num text-2xl pt-1 w-8">
                      0{i + 1}
                    </span>
                    <span className="flex-1 text-lg text-white border-b border-white/10 pb-4 group-last:border-0 group-hover:text-gold transition-colors">
                      {it}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-7 text-[13px] text-white/55 italic">
                Not just another enquiry form.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- */
/*  Section: Consultancy emotional hook (replaces pricing #2)     */
/* -------------------------------------------------------------- */
function EmotionalHookSection() {
  const focus = [
    "Serious students",
    "Interested parents",
    "Scholarship-qualified applicants",
  ];
  return (
    <section
      data-testid="saas-emotional"
      className="relative bg-ivory grain-soft"
    >
      <div className="relative z-10 mx-auto max-w-5xl px-6 lg:px-10 py-24 lg:py-32 text-center">
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold flex items-center justify-center gap-2">
          <span className="h-px w-8 bg-gold/60" /> For the people who run the floor
        </div>
        <h2 className="mt-6 font-serif-display text-3xl sm:text-4xl lg:text-[52px] leading-[1.06] text-[#0A2540]">
          Your counsellors should be
          <span className="italic text-gold"> closing admissions </span>
          — not chasing random leads.
        </h2>
        <p className="mt-7 max-w-2xl mx-auto text-[#0A2540]/70 text-base leading-relaxed">
          The platform helps your team stop reacting to noise and start working only on
          the people who actually convert.
        </p>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
          {focus.map((f) => (
            <div
              key={f}
              className="rounded-xl border border-[#0A2540]/10 bg-white p-6 hover:border-gold/40 hover:shadow-[0_18px_40px_-18px_rgba(201,169,97,0.4)] transition-all duration-500"
            >
              <Check className="h-5 w-5 text-gold mx-auto" strokeWidth={2.5} />
              <div className="mt-3 font-serif-display text-lg text-[#0A2540]">{f}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- */
/*  Section: Customer proof                                       */
/* -------------------------------------------------------------- */
function ProofSection() {
  const { openWith } = useInquiry();
  const small = [
    {
      quote: "I stopped using sticky notes. The Tasks panel runs my day.",
      who: "Counsellor · Sunrise MBBS",
    },
    {
      quote: "Finally know which Facebook campaign is actually working.",
      who: "Admin · Apex Med Abroad",
    },
    {
      quote: "Got my son's scholarship slab on WhatsApp in 2 days. Felt premium.",
      who: "Parent · Pune",
    },
  ];

  return (
    <section
      id="proof"
      data-testid="saas-proof"
      className="relative bg-white"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold flex items-center gap-2">
          <span className="h-px w-8 bg-gold/60" /> Proof
        </div>
        <h2 className="mt-5 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-[#0A2540] max-w-3xl">
          Quiet operators. <span className="italic text-gold">Loud results.</span>
        </h2>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Featured */}
          <div className="lg:col-span-7 relative rounded-2xl bg-[#0A2540] text-white p-10 lg:p-14 overflow-hidden">
            <Quote className="absolute top-8 right-8 h-16 w-16 text-gold/15" />
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold">
              Featured story
            </div>
            <p className="mt-6 font-serif-display text-2xl sm:text-3xl leading-[1.25] text-white/95">
              "We went from 80 leads in our Excel sheet to 600 in MedAdmits AI in 3 months.
              Counsellors actually use it — that's the surprising part. Conversion is up
              <span className="text-gold"> 42%.</span>"
            </p>
            <div className="mt-8 flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center font-serif-display text-gold text-lg">
                AS
              </div>
              <div>
                <div className="text-white font-medium">Amit Sharma</div>
                <div className="text-white/60 text-sm">Founder · Sunrise MBBS · Pune</div>
              </div>
            </div>
          </div>

          {/* Smaller */}
          <div className="lg:col-span-5 grid grid-cols-1 gap-4">
            {small.map((s) => (
              <div
                key={s.who}
                className="rounded-xl border border-[#0A2540]/10 bg-[#FAF9F6] p-7 hover:bg-white hover:border-gold/40 transition-colors duration-500"
              >
                <Quote className="h-4 w-4 text-gold" />
                <p className="mt-3 text-[#0A2540] text-[15px] leading-relaxed">{s.quote}</p>
                <div className="mt-4 text-xs uppercase tracking-wider text-[#0A2540]/55">
                  {s.who}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mini case study */}
        <div className="mt-10 rounded-2xl border border-[#0A2540]/10 bg-[#FAF9F6] p-8 lg:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6 hover:border-gold/40 transition-colors">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold">Case study</div>
            <div className="mt-3 font-serif-display text-2xl text-[#0A2540]">
              How Sunrise grew from 12 closures/year to 45 in 8 months.
            </div>
          </div>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              openWith("case_study");
            }}
            className="inline-flex items-center gap-2 text-[#0A2540] font-medium link-shine"
            data-testid="case-study-cta"
          >
            Read the playbook
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- */
/*  Section: How it works (3-step timeline)                       */
/* -------------------------------------------------------------- */
function HowItWorksSection() {
  const steps = [
    {
      day: "Day 1",
      title: "Sign up + branded landing live",
      body:
        "Pick a slug (sunrise.medadmits.com), upload your logo, choose colors. Done in 10 minutes.",
    },
    {
      day: "Week 1",
      title: "Counsellors trained + first campaign live",
      body:
        "60-minute recorded onboarding. Your team is closing tickets by day 5.",
    },
    {
      day: "Day 30",
      title: "First ROI report",
      body:
        "We sit with you to review: leads in, leads closed, attribution by source. Decide what to scale.",
    },
  ];
  return (
    <section
      data-testid="saas-howitworks"
      className="relative bg-ivory grain-soft"
    >
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold flex items-center gap-2">
          <span className="h-px w-8 bg-gold/60" /> Rollout
        </div>
        <h2 className="mt-5 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-[#0A2540] max-w-3xl">
          Live in 7 days. <span className="italic text-gold">Compounding from day 30.</span>
        </h2>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          <div className="hidden md:block absolute top-12 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#0A2540]/15 to-transparent" />
          {steps.map((s, i) => (
            <div key={s.day} className="relative">
              <div className="relative z-10 h-12 w-12 rounded-full bg-[#0A2540] text-gold font-serif-display flex items-center justify-center text-lg shadow-[0_10px_30px_-10px_rgba(10,37,64,0.5)]">
                {i + 1}
              </div>
              <div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-gold">
                {s.day}
              </div>
              <div className="mt-2 font-serif-display text-2xl text-[#0A2540] leading-tight">
                {s.title}
              </div>
              <div className="mt-3 text-[#0A2540]/65 leading-relaxed">{s.body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- */
/*  Section: ROI calculator (premium interactive)                  */
/* -------------------------------------------------------------- */
function RoiCalculatorSection() {
  const [leads, setLeads] = useState([300]);
  const monthlyLeads = leads[0];
  const counsellorCost = 35000;
  const savings = useMemo(() => {
    // crude estimate: 1 counsellor needed per 200 leads/month manually
    const counsellorsSaved = Math.max(0, Math.floor(monthlyLeads / 200) - 1);
    const adWaste = Math.round(monthlyLeads * 110); // ₹ wasted per low-intent lead
    return {
      counsellors: counsellorsSaved,
      manpower: counsellorsSaved * counsellorCost,
      ads: adWaste,
      total: counsellorsSaved * counsellorCost + adWaste,
    };
  }, [monthlyLeads]);

  const tier =
    monthlyLeads < 250 ? "Studio" : monthlyLeads < 800 ? "Growth" : "Scale";

  return (
    <section
      data-testid="saas-roi-calc"
      className="relative bg-white"
    >
      <div className="mx-auto max-w-6xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold flex items-center gap-2">
              <Calculator className="h-4 w-4" /> ROI calculator
            </div>
            <h2 className="mt-5 font-serif-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.08] text-[#0A2540]">
              How much would <span className="italic text-gold">MedAdmits AI</span> save you
              every month?
            </h2>
            <p className="mt-5 text-[#0A2540]/70">
              Drag the slider. We'll show what the platform would replace in your current
              cost stack.
            </p>
          </div>

          <div className="lg:col-span-7 rounded-2xl border border-[#0A2540]/10 bg-[#FAF9F6] p-8">
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#0A2540]/55">
                Monthly leads
              </div>
              <div className="font-mono-clock text-2xl text-[#0A2540]">
                {monthlyLeads.toLocaleString("en-IN")}
              </div>
            </div>
            <div className="mt-4">
              <Slider
                value={leads}
                onValueChange={setLeads}
                min={50}
                max={2000}
                step={50}
                data-testid="roi-slider"
              />
              <div className="mt-2 flex justify-between text-[10px] text-[#0A2540]/45 font-mono-clock">
                <span>50</span>
                <span>2,000</span>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="rounded-lg bg-white border border-[#0A2540]/10 p-5">
                <div className="text-[10px] uppercase tracking-wider text-[#0A2540]/55">
                  Manpower saved
                </div>
                <div className="mt-2 font-serif-display text-2xl text-[#0A2540]">
                  ₹{savings.manpower.toLocaleString("en-IN")}
                </div>
                <div className="text-[11px] text-[#0A2540]/55">
                  ≈ {savings.counsellors} counsellor(s)
                </div>
              </div>
              <div className="rounded-lg bg-white border border-[#0A2540]/10 p-5">
                <div className="text-[10px] uppercase tracking-wider text-[#0A2540]/55">
                  Ad waste reclaimed
                </div>
                <div className="mt-2 font-serif-display text-2xl text-[#0A2540]">
                  ₹{savings.ads.toLocaleString("en-IN")}
                </div>
                <div className="text-[11px] text-[#0A2540]/55">low-intent leads filtered</div>
              </div>
            </div>

            <div className="mt-5 rounded-lg bg-[#0A2540] text-white p-6 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">
                  Estimated monthly impact
                </div>
                <div className="mt-1 font-serif-display text-3xl">
                  ₹{savings.total.toLocaleString("en-IN")}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">
                  Recommended tier
                </div>
                <div className="mt-1 font-serif-display text-2xl text-gold">{tier}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- */
/*  Section: FAQ                                                  */
/* -------------------------------------------------------------- */
function FaqSection() {
  const faqs = [
    {
      q: "Is my data isolated from other consultancies?",
      a: "Yes. Hard tenant isolation at the database level. Nobody — including other consultancies — can see your leads, counsellors, or campaigns.",
    },
    {
      q: "Can I import existing leads from Excel?",
      a: "Yes. Drag-drop CSV upload with auto-deduplication by email and phone. We map columns intelligently so you don't lose history.",
    },
    {
      q: "Do students need to install an app?",
      a: "No. It's a web-based experience that runs on any phone browser. Branded with your logo, your domain.",
    },
    {
      q: "Can I customize the scholarship test?",
      a: "Yes. Upload your own MCQs (CSV) or use our default NEET-aligned bank. You can also append your questions on top of the default set.",
    },
    {
      q: "What if I cancel? Do I lose my data?",
      a: "Export everything to CSV any time. Cancel any time. No retention games, no exit fees.",
    },
    {
      q: "Do you offer Hindi support?",
      a: "Bilingual landing copy + WhatsApp templates today. Full Hindi UI is on the roadmap and available on request.",
    },
    {
      q: "Can multiple consultancies share one account?",
      a: "No — each gets their own isolated tenant + branded landing. That's the whole point of MedAdmits AI.",
    },
    {
      q: "How fast is implementation?",
      a: "7 days from signup to first lead processed. Logo, colors, slug, default test live in 10 minutes. Counsellor onboarding within the first week.",
    },
  ];
  return (
    <section
      id="faq"
      data-testid="saas-faq"
      className="relative bg-ivory grain-soft"
    >
      <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold flex items-center gap-2">
          <span className="h-px w-8 bg-gold/60" /> Honest answers
        </div>
        <h2 className="mt-5 font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-[#0A2540]">
          Things we get asked <span className="italic text-gold">on every demo.</span>
        </h2>

        <Accordion type="single" collapsible className="mt-10 divide-y divide-[#0A2540]/10 border-y border-[#0A2540]/10">
          {faqs.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="border-0"
            >
              <AccordionTrigger className="py-6 text-left text-[#0A2540] font-serif-display text-lg sm:text-xl hover:no-underline hover:text-gold transition-colors">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#0A2540]/70 text-[15px] leading-relaxed pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- */
/*  Section: Risk reversal banner                                 */
/* -------------------------------------------------------------- */
function RiskReversalSection() {
  return (
    <section
      data-testid="saas-risk"
      className="relative bg-[#0A2540] text-white grain-soft grain-dark"
    >
      <div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-10 py-20 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-2 flex md:justify-center">
          <div className="h-16 w-16 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center text-gold">
            <ShieldCheck className="h-7 w-7" />
          </div>
        </div>
        <div className="md:col-span-10">
          <h3 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl leading-[1.15]">
            Concerned about investing in another platform?
          </h3>
          <p className="mt-4 text-gold/85 text-base sm:text-lg leading-relaxed max-w-3xl">
            You won't be paying us — you'll be redirecting money you're already losing on
            random ads into a system that compounds. <span className="italic text-white">
            Technically, you're saving a lot.</span>
          </p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- */
/*  Section: Final CTA                                            */
/* -------------------------------------------------------------- */
function FinalCtaSection() {
  const { openWith } = useInquiry();
  return (
    <section
      id="book-demo"
      data-testid="saas-final-cta"
      className="relative bg-[#061a30] text-white grain-soft grain-dark"
    >
      <div className="relative z-10 mx-auto max-w-5xl px-6 lg:px-10 py-28 lg:py-36 text-center">
        <Sparkles className="mx-auto h-6 w-6 text-gold" />
        <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.04]">
          Ready to give your consultancy
          <br />
          <span className="italic text-gold">an unfair advantage?</span>
        </h2>
        <p className="mt-7 text-gold/80 text-base sm:text-lg">
          30-minute demo · Custom-branded sandbox · No commitment.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => openWith("unfair_advantage")}
            data-testid="final-cta-book"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-[#0A2540] font-medium hover:bg-[#d8b977] transition-colors"
          >
            Book demo
            <ArrowRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => openWith("download_deck")}
            data-testid="final-cta-deck"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/30 text-white hover:border-gold hover:text-gold transition-colors"
          >
            Get the sales deck
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-16 mx-auto max-w-2xl rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-left">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold">
            Prefer to leave details first?
          </div>
          <p className="mt-3 text-white/70 text-sm leading-relaxed">
            Use any of the buttons above. We collect Name, Phone, WhatsApp, Email,
            Company name, Location and your best time to connect — and a senior
            team member calls you back within one business day.
          </p>
          <button
            onClick={() => openWith("callback")}
            data-testid="final-cta-callback"
            className="mt-5 inline-flex items-center gap-2 text-gold link-shine text-sm"
          >
            Open the form
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- */
/*  Footer + live counter                                          */
/* -------------------------------------------------------------- */
function LiveCounterFooter() {
  const [n, setN] = useState(12847);
  useEffect(() => {
    const id = setInterval(
      () => setN((v) => v + Math.floor(Math.random() * 4) + 1),
      4500
    );
    return () => clearInterval(id);
  }, []);
  return (
    <footer
      data-testid="saas-footer"
      className="bg-ivory border-t border-[#0A2540]/10"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
        <div className="flex items-center gap-3 text-[#0A2540]">
          <div className="font-serif-display text-2xl">
            Med<span className="italic text-gold">Admits</span>
          </div>
        </div>
        <div className="text-center">
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#0A2540]/55">
            Live · MBBS leads scored this month
          </div>
          <div className="mt-2 font-mono-clock text-2xl text-[#0A2540]">
            {n.toLocaleString("en-IN")}
          </div>
        </div>
        <div className="md:text-right text-xs text-[#0A2540]/55">
          © 2026 Meda Powered by Medadmits,
          <br />
          Made for Admission Conversions.
        </div>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------- */
/*  Page                                                          */
/* -------------------------------------------------------------- */
function MedaAiHeroSection() {
  const { openWith } = useInquiry();
  return <MedaAiHero onBookDemo={() => openWith("book_demo")} />;
}

export default function SaasSalesPage() {
  // hash-scroll smoothing
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = "smooth";
    return () => {
      html.style.scrollBehavior = prev;
    };
  }, []);

  return (
    <InquiryProvider>
      <main data-testid="saas-sales-page" className="bg-ivory text-[#0A2540]">
        <StickyNav />
        <MedaAiHeroSection />
        <HeroSection />
        <PainSection />
        <SolutionSection />
        <ProductShowcaseSection />
        <DifferentiatorsSection />
        <RoiFocusSection />
        <EmotionalHookSection />
        <HowItWorksSection />
        <RoiCalculatorSection />
        <FaqSection />
        <RiskReversalSection />
        <FinalCtaSection />
        <LiveCounterFooter />
      </main>
    </InquiryProvider>
  );
}
